/**
 * Shared helpers for enrollment JSON payloads.
 * Backend stores nested JSON; UI always shows labeled key/value fields.
 */

export const ENROLLMENT_STATUSES = {
  DRAFT: "DRAFT",
  PENDING: "PENDING",
  IN_REVIEW: "IN_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
};

/**
 * Per-type keys returned by backend enrollmentStatistics JSON.
 * Prefer these over aggregated statusCounts so each home page
 * shows metrics for its own enrollment type only.
 */
export const ENROLLMENT_STATS_TYPE_KEYS = {
  insuree: "insuree_enrollments",
  family: "family_enrollments",
  healthFacility: "health_facility_enrollments",
  policyHolder: "policyholder_enrollments",
};

/** Extract status counts for a specific enrollment type from enrollmentStatistics. */
export function getEnrollmentTypeStatusCounts(statistics, typeKey) {
  if (!statistics || !typeKey) return {};
  const bucket =
    statistics[typeKey] ||
    statistics[ENROLLMENT_STATS_TYPE_KEYS[typeKey]] ||
    null;
  const byStatus =
    (bucket?.by_status && typeof bucket.by_status === "object"
      ? bucket.by_status
      : null) ||
    (bucket?.byStatus && typeof bucket.byStatus === "object"
      ? bucket.byStatus
      : null) ||
    {};

  const summed = Object.values(byStatus).reduce(
    (acc, n) => acc + (Number(n) || 0),
    0
  );
  const total =
    typeof bucket?.total === "number" ? bucket.total : summed;

  return {
    ...byStatus,
    TOTAL: total,
  };
}

/**
 * Navigate to the core entity created when an enrollment was approved.
 * Uses module route refs when available; falls back to known paths.
 */
export function openCreatedEnrollmentEntity(modulesManager, history, type, enrollment) {
  if (!enrollment || enrollment.status !== "APPROVED") return false;

  const push = (path) => {
    if (history) history.push(path.startsWith("/") ? path : `/${path}`);
    else {
      const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      window.location.href = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    }
  };

  const refPath = (refKey, params = []) => {
    const ref = modulesManager?.getRef?.(refKey);
    if (!ref) return null;
    const suffix = params.filter(Boolean).join("/");
    return `/${ref}${suffix ? `/${suffix}` : ""}`;
  };

  if (type === "insuree" || type === "accountClaim") {
    const uuid =
      type === "accountClaim"
        ? enrollment.targetInsuree?.uuid
        : enrollment.createdInsuree?.uuid;
    if (!uuid) return false;
    const path =
      refPath("insuree.route.insuree", [uuid]) ||
      `/insuree/insurees/insuree/${uuid}`;
    push(path);
    return true;
  }

  if (type === "family") {
    const uuid = enrollment.createdFamily?.uuid;
    if (!uuid) return false;
    const path =
      refPath("insuree.route.familyOverview", [uuid]) ||
      `/insuree/families/familyOverview/${uuid}`;
    push(path);
    return true;
  }

  if (type === "healthFacility") {
    const uuid = enrollment.createdHealthFacility?.uuid;
    if (!uuid) return false;
    const path =
      refPath("location.route.healthFacilityEdit", [uuid]) ||
      `/location/healthFacility/${uuid}`;
    push(path);
    return true;
  }

  if (type === "policyHolder") {
    const id = enrollment.createdPolicyholder?.id;
    if (id == null) return false;
    // Policy holder routes expect decoded numeric id
    const path =
      refPath("policyHolder.route.policyHolder", [String(id)]) ||
      `/formalSectors/formalSectors/${id}`;
    push(path);
    return true;
  }

  return false;
}

/** Safely parse GraphQL JSONString / object / double-encoded string. */
export function parseJsonField(raw) {
  if (raw == null || raw === "") return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  if (typeof raw !== "string") return {};
  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        return {};
      }
    }
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (e) {
    return {};
  }
}

/** Pick first defined non-empty value. */
export function pick(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

/** Format a value for display (never dump raw objects). */
export function formatDisplayValue(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    return value
      .map((item) =>
        typeof item === "object" ? JSON.stringify(item) : String(item)
      )
      .join(", ");
  }
  if (typeof value === "object") {
    // Prefer common name/code shapes over raw JSON
    const nested = pick(
      value.name,
      value.label,
      value.code,
      value.username,
      value.chfId,
      value.chf_id
    );
    if (nested != null) return String(nested);
    return "—";
  }
  return String(value);
}

export function toJsonStringInput(value) {
  if (value == null) return null;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function humanizeKey(key) {
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build ordered key/value rows from a map of label → value.
 * Skips empty rows unless includeEmpty is true.
 */
export function buildFieldRows(entries, { includeEmpty = false } = {}) {
  return entries
    .map(([label, value]) => ({
      label,
      value: formatDisplayValue(value),
      raw: value,
    }))
    .filter((row) => includeEmpty || (row.raw !== null && row.raw !== undefined && row.raw !== ""));
}

/** Normalize insuree JSON into a stable camelCase view model. */
export function normalizeInsureeData(raw) {
  const data = parseJsonField(raw);
  return {
    chfId: pick(data.chfId, data.chf_id),
    lastName: pick(data.lastName, data.last_name),
    otherNames: pick(data.otherNames, data.other_names, data.firstName, data.first_name),
    dob: pick(data.dob, data.dateOfBirth, data.date_of_birth),
    gender: pick(data.gender_id, data.genderId, data.gender),
    phone: pick(data.phone, data.phoneNumber),
    email: pick(data.email),
    address: pick(data.current_address, data.currentAddress, data.address),
    marital: pick(data.marital, data.maritalStatus),
    profession: pick(data.profession_id, data.professionId, data.profession, data.occupation),
    education: pick(data.education_id, data.educationId, data.education),
    relationship: pick(data.relationship_id, data.relationshipId, data.relationship),
    identification: pick(
      data.passport,
      data.identification_number,
      data.idNumber,
      data.identification
    ),
    healthFacility: pick(
      data.health_facility_id,
      data.healthFacilityId,
      data.health_facility,
      data.healthFacility
    ),
    cardIssued: pick(data.card_issued, data.cardIssued),
    head: pick(data.head, data.isHead),
    status: pick(data.status),
    photo: pick(data.photo),
    _raw: data,
  };
}

export function insureeFieldRows(rawOrNormalized) {
  const d =
    rawOrNormalized && rawOrNormalized._raw
      ? rawOrNormalized
      : normalizeInsureeData(rawOrNormalized);
  return buildFieldRows([
    ["CHF ID", d.chfId],
    ["Last name", d.lastName],
    ["Other names", d.otherNames],
    ["Date of birth", d.dob],
    ["Gender", d.gender],
    ["Phone", d.phone],
    ["Email", d.email],
    ["Address", d.address],
    ["Marital status", d.marital],
    ["Profession", d.profession],
    ["Education", d.education],
    ["Relationship", d.relationship],
    ["Identification", d.identification],
    ["Health facility", d.healthFacility],
    ["Card issued", d.cardIssued],
    ["Head of family", d.head],
    ["Insuree status", d.status],
  ]);
}

/** Map UI insuree form → backend InsureeService-oriented JSON. */
export function toBackendInsureePayload(form) {
  const data = form || {};
  const payload = {
    chf_id: pick(data.chf_id, data.chfId),
    last_name: pick(data.last_name, data.lastName),
    other_names: pick(data.other_names, data.otherNames, data.firstName),
    dob: pick(data.dob, data.dateOfBirth),
    gender_id: pick(data.gender_id, data.genderId, data.gender),
    phone: data.phone || undefined,
    email: data.email || undefined,
    current_address: pick(data.current_address, data.currentAddress, data.address),
    marital: pick(data.marital, data.maritalStatus),
    passport: pick(data.passport, data.idNumber, data.identification),
    card_issued: Boolean(pick(data.card_issued, data.cardIssued, false)),
    head: Boolean(pick(data.head, data.isHead, false)),
    status: pick(data.status, "AC"),
  };
  const profession = pick(data.profession_id, data.professionId, data.profession);
  if (profession != null && profession !== "") payload.profession_id = profession;
  const education = pick(data.education_id, data.educationId, data.education);
  if (education != null && education !== "") payload.education_id = education;
  const relationship = pick(
    data.relationship_id,
    data.relationshipId,
    data.relationship
  );
  if (relationship != null && relationship !== "") {
    payload.relationship_id = relationship;
  }
  const hf = pick(
    data.health_facility_id,
    data.healthFacilityId,
    data.health_facility,
    data.healthFacility
  );
  if (hf != null && hf !== "") payload.health_facility_id = hf;
  if (data.photo) payload.photo = data.photo;
  // Drop undefined keys
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });
  return payload;
}

export function normalizeFamilyData(raw) {
  const data = parseJsonField(raw);
  const head = parseJsonField(data.head_insuree || data.headInsuree || {});
  return {
    locationId: pick(data.location_id, data.locationId, data.location),
    address: pick(data.address),
    poverty: pick(data.poverty, false),
    familyType: pick(data.family_type_id, data.familyType, data.family_type),
    confirmationNo: pick(data.confirmation_no, data.confirmationNo),
    confirmationType: pick(
      data.confirmation_type_id,
      data.confirmationType,
      data.confirmation_type
    ),
    ethnicity: pick(data.ethnicity),
    isOffline: pick(data.is_offline, data.isOffline, false),
    headChfId: pick(head.chf_id, head.chfId, data.headInsureeChfId),
    headLastName: pick(head.last_name, head.lastName),
    headOtherNames: pick(head.other_names, head.otherNames, head.firstName),
    headDob: pick(head.dob, head.dateOfBirth),
    headGender: pick(head.gender_id, head.genderId, head.gender),
    headPhone: pick(head.phone),
    _raw: data,
    _head: head,
  };
}

export function familyFieldRows(rawOrNormalized) {
  const d =
    rawOrNormalized && rawOrNormalized._raw
      ? rawOrNormalized
      : normalizeFamilyData(rawOrNormalized);
  return buildFieldRows([
    ["Location", d.locationId],
    ["Address", d.address],
    ["Poverty", d.poverty],
    ["Family type", d.familyType],
    ["Confirmation no.", d.confirmationNo],
    ["Confirmation type", d.confirmationType],
    ["Ethnicity", d.ethnicity],
    ["Offline", d.isOffline],
    ["Head CHF ID", d.headChfId],
    ["Head last name", d.headLastName],
    ["Head other names", d.headOtherNames],
    ["Head date of birth", d.headDob],
    ["Head gender", d.headGender],
    ["Head phone", d.headPhone],
  ]);
}

export function toBackendFamilyPayload(form) {
  const data = form || {};
  const headSource = data.head_insuree || data.headInsuree || data;
  const head = toBackendInsureePayload({
    chfId: pick(headSource.chf_id, headSource.chfId, data.headChfId, data.headInsureeChfId),
    lastName: pick(headSource.last_name, headSource.lastName, data.headLastName),
    otherNames: pick(
      headSource.other_names,
      headSource.otherNames,
      headSource.firstName,
      data.headOtherNames,
      data.headFirstName
    ),
    dob: pick(headSource.dob, headSource.dateOfBirth, data.headDob),
    gender: pick(headSource.gender_id, headSource.gender, data.headGender),
    phone: pick(headSource.phone, data.headPhone),
    cardIssued: pick(headSource.card_issued, headSource.cardIssued, false),
    head: true,
    status: "AC",
  });
  const payload = {
    location_id: pick(data.location_id, data.locationId, data.location),
    address: data.address || undefined,
    poverty: Boolean(data.poverty),
    family_type_id: pick(data.family_type_id, data.familyType, data.family_type),
    confirmation_no: pick(data.confirmation_no, data.confirmationNo),
    confirmation_type_id: pick(
      data.confirmation_type_id,
      data.confirmationType,
      data.confirmation_type
    ),
    ethnicity: data.ethnicity || undefined,
    is_offline: Boolean(pick(data.is_offline, data.isOffline, false)),
    head_insuree: head,
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });
  return payload;
}

export function normalizeFacilityData(raw) {
  const data = parseJsonField(raw);
  return {
    code: pick(data.code, data.facility_code, data.facilityCode),
    name: pick(data.name, data.facility_name, data.facilityName),
    legalFormId: pick(data.legal_form_id, data.legalFormId, data.legal_form, data.legalForm),
    level: pick(data.level),
    careType: pick(data.care_type, data.careType),
    locationId: pick(data.location_id, data.locationId, data.location),
    address: pick(data.address),
    phone: pick(data.phone),
    fax: pick(data.fax),
    email: pick(data.email),
    status: pick(data.status, "AC"),
    offline: pick(data.offline, false),
    contractStart: pick(data.contract_start_date, data.contractStartDate),
    contractEnd: pick(data.contract_end_date, data.contractEndDate),
    _raw: data,
  };
}

export function facilityFieldRows(rawOrNormalized) {
  const d =
    rawOrNormalized && rawOrNormalized._raw
      ? rawOrNormalized
      : normalizeFacilityData(rawOrNormalized);
  return buildFieldRows([
    ["Facility code", d.code],
    ["Facility name", d.name],
    ["Legal form", d.legalFormId],
    ["Level", d.level],
    ["Care type", d.careType],
    ["Location", d.locationId],
    ["Address", d.address],
    ["Phone", d.phone],
    ["Fax", d.fax],
    ["Email", d.email],
    ["Status", d.status],
    ["Offline", d.offline],
    ["Contract start", d.contractStart],
    ["Contract end", d.contractEnd],
  ]);
}

export function toBackendFacilityPayload(form) {
  const data = form || {};
  const payload = {
    facility_code: pick(data.facility_code, data.facilityCode, data.code),
    facility_name: pick(data.facility_name, data.facilityName, data.name),
    code: pick(data.code, data.facility_code, data.facilityCode),
    name: pick(data.name, data.facility_name, data.facilityName),
    legal_form_id: pick(data.legal_form_id, data.legalFormId, data.legalForm, data.legal_form),
    level: data.level || undefined,
    care_type: pick(data.care_type, data.careType),
    location_id: pick(data.location_id, data.locationId, data.location),
    address: data.address || undefined,
    phone: data.phone || undefined,
    fax: data.fax || undefined,
    email: data.email || undefined,
    status: pick(data.status, "AC"),
    offline: Boolean(data.offline),
    contract_start_date: pick(data.contract_start_date, data.contractStartDate),
    contract_end_date: pick(data.contract_end_date, data.contractEndDate),
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });
  return payload;
}

export function normalizePolicyholderData(raw) {
  const data = parseJsonField(raw);
  return {
    code: pick(data.code, data.policyholder_code, data.policyholderCode),
    name: pick(data.trade_name, data.tradeName, data.policyholder_name, data.policyholderName, data.name, data.companyName),
    type: pick(data.policyholder_type, data.policyholderType, data.type),
    registrationNumber: pick(data.registration_number, data.registrationNumber),
    email: pick(data.email, data.contact_email, data.contactEmail),
    phone: pick(data.phone, data.contact_phone, data.contactPhone),
    locationId: pick(data.locations_id, data.location_id, data.locationId, data.location),
    expectedMembers: pick(data.expected_members, data.expectedMembers),
    address: pick(data.address),
    contactName: pick(data.contact_name, data.contactName, data.contactPerson),
    _raw: data,
  };
}

export function policyholderFieldRows(rawOrNormalized) {
  const d =
    rawOrNormalized && rawOrNormalized._raw
      ? rawOrNormalized
      : normalizePolicyholderData(rawOrNormalized);
  return buildFieldRows([
    ["Code", d.code],
    ["Name", d.name],
    ["Type", d.type],
    ["Registration number", d.registrationNumber],
    ["Email", d.email],
    ["Phone", d.phone],
    ["Location", d.locationId],
    ["Expected members", d.expectedMembers],
    ["Contact name", d.contactName],
    ["Address", typeof d.address === "object" ? formatDisplayValue(d.address) : d.address],
  ]);
}

export function toBackendPolicyholderPayload(form) {
  const data = form || {};
  const payload = {
    policyholder_code: pick(data.policyholder_code, data.policyholderCode, data.code),
    policyholder_name: pick(
      data.policyholder_name,
      data.policyholderName,
      data.trade_name,
      data.tradeName,
      data.companyName,
      data.name
    ),
    code: pick(data.code, data.policyholder_code, data.policyholderCode),
    trade_name: pick(
      data.trade_name,
      data.tradeName,
      data.policyholder_name,
      data.companyName,
      data.name
    ),
    policyholder_type: pick(data.policyholder_type, data.policyholderType, data.type),
    registration_number: pick(data.registration_number, data.registrationNumber),
    contact_email: pick(data.contact_email, data.contactEmail, data.email),
    contact_phone: pick(data.contact_phone, data.contactPhone, data.phone),
    email: pick(data.email, data.contact_email, data.contactEmail),
    phone: pick(data.phone, data.contact_phone, data.contactPhone),
    locations_id: pick(data.locations_id, data.location_id, data.locationId, data.location),
    expected_members: pick(data.expected_members, data.expectedMembers),
    address: data.address || undefined,
    contact_name: pick(data.contact_name, data.contactName, data.contactPerson),
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });
  return payload;
}

/** Map UI claim form → backend claimData JSON. */
export function normalizeClaimData(raw) {
  const data = parseJsonField(raw);
  return {
    fullName: pick(data.full_name, data.fullName, data.name),
    dob: pick(data.dob, data.dateOfBirth),
    chfId: pick(data.chf_id, data.chfId),
    notes: pick(data.notes),
    _raw: data,
  };
}

export function claimFieldRows(rawOrNormalized) {
  const d =
    rawOrNormalized && rawOrNormalized._raw
      ? rawOrNormalized
      : normalizeClaimData(rawOrNormalized);
  return buildFieldRows([
    ["CHF ID", d.chfId],
    ["Full name", d.fullName],
    ["Date of birth", d.dob],
    ["Claim notes", d.notes],
  ]);
}

/** Rows for the live Insuree record a claim is trying to link to. */
export function targetInsureeFieldRows(targetInsuree) {
  if (!targetInsuree) return [];
  const gender =
    typeof targetInsuree.gender === "object"
      ? pick(targetInsuree.gender?.gender, targetInsuree.gender?.code)
      : targetInsuree.gender;
  const hf =
    typeof targetInsuree.healthFacility === "object"
      ? pick(
          targetInsuree.healthFacility?.name,
          targetInsuree.healthFacility?.code
        )
      : targetInsuree.healthFacility;
  return buildFieldRows([
    ["CHF ID", targetInsuree.chfId],
    ["Last name", targetInsuree.lastName],
    ["Other names", targetInsuree.otherNames],
    ["Date of birth", targetInsuree.dob],
    ["Age", targetInsuree.age],
    ["Gender", gender],
    ["Phone", targetInsuree.phone],
    ["Email", targetInsuree.email],
    ["Address", targetInsuree.currentAddress],
    ["Marital status", targetInsuree.marital],
    ["Identification", targetInsuree.passport],
    ["Head of family", targetInsuree.head],
    ["Insuree status", targetInsuree.status],
    ["Health facility", hf],
  ]);
}

export function relatedInsureeDisplayName(insuree) {
  if (!insuree) return "—";
  const last = formatDisplayValue(insuree.lastName);
  const other = formatDisplayValue(insuree.otherNames);
  if (last === "—" && other === "—") return "—";
  if (other === "—") return last;
  if (last === "—") return other;
  return `${last}, ${other}`;
}

export function toBackendClaimPayload(form) {
  const data = form || {};
  const payload = {
    full_name: pick(data.full_name, data.fullName, data.name),
    dob: pick(data.dob, data.dateOfBirth),
    chf_id: pick(data.chf_id, data.chfId),
    notes: pick(data.notes),
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });
  return payload;
}

/** Common enrollment meta rows (status, audit). */
export function enrollmentMetaRows(enrollment) {
  if (!enrollment) return [];
  return buildFieldRows([
    ["Status", enrollment.status],
    ["Submitted by type", enrollment.submittedByType],
    ["Other submitted by", enrollment.otherSubmittedBy],
    ["Created", enrollment.dateCreated],
    ["Approval date", enrollment.approvalDate],
    ["Created by", enrollment.createdBy?.username || enrollment.createdBy?.lastName],
    ["Approved by", enrollment.approvedBy?.username || enrollment.approvedBy?.lastName],
    ["Rejection reason", enrollment.reasonForRejection],
    ["Notes", enrollment.notes],
  ]);
}

/** Fallback: show remaining unknown keys as humanized labels (never raw JSON blob). */
export function extraFieldRows(raw, knownKeys = []) {
  const data = parseJsonField(raw);
  const skip = new Set(knownKeys);
  return Object.keys(data)
    .filter((k) => !skip.has(k) && !k.startsWith("_"))
    .filter((k) => {
      const v = data[k];
      return v !== null && v !== undefined && v !== "" && typeof v !== "object";
    })
    .map((k) => ({
      label: humanizeKey(k),
      value: formatDisplayValue(data[k]),
      raw: data[k],
    }));
}
